import * as React from 'react';
import { SingleKeyValueComponent } from 'ui/common';
import { compose } from 'redux';
import styles from './display-number.scss';
import { Label, NoValueDash } from 'ui/common';
import { ReactNode } from 'react';

export interface DisplayNumberProps extends SingleKeyValueComponent<number> {
  /**
   * Determine locale used. Default is fallback to current browser settings. Default is English.
   */
  locale?: any;
  /**
   * Predefined possible styles by the design handbook
   */
  style?: 'primary' | 'secondary';
  /**
   * When true, the label font is normal and the value font is bold
   */
  strong?: boolean;  
  /**
   * When true, a predefined style class to render the label above the value is applied
   */
  showStacked: boolean;
  /**
   * When true, the component gets hidden when value is empty
   */
  hideIfNoValue?: boolean;
  /**
   * When true, thousand separator is enabled and separate values depending on the locale being used. Default is true.
   * -If decimal separator supported by the locale is dot then thousand separator is comma.
   * -If decimal separator supported by the locale is comma then thousand separator is dot.   
   */
  useThousand?: boolean;
  /**
   * A prefix string to render in front of the number
   */
  prefix?: string;
  /**
   * A suffix string to render next to or after the number
   */
  suffix?: string;
  /**
   * When true, the component gets aligned to the end.
   * Hence, when LTR(left-to-right direction) is in effect it's pulled to the right end, else pulled to the left end.
   */
  alignToEnd?: boolean;
  /**
   * When true, both the label and the value fonts are bold and label is in uppercase. 
   * Casing of numbers does not make sense, therefore the inversion is applied to the label instead.
   */
  invertStrong?: boolean;
  /**
   * Set maximum number of digits in decimals part. Default is 3.
   */
  maxDecimals?: number;
  /**
   * Text label to display
   */
  textLabel?: string;

  /**
   * @internal
   */
  children?: ReactNode;
}

const DisplayNumberLabel = ({ label, textLabel }: Pick<DisplayNumberProps, 'label' | 'textLabel'>) =>
  textLabel ? <span className={styles.textLabel}>{textLabel}</span> : <Label label={label} />;

/*
 * Returns true if the input can be converted to a number, including numeric 0
 */
export const isNumeric = (s: string | number) => typeof s === 'number' && Number.isFinite(s) ||  
                                (typeof s === 'string' && s.trim() !== '' && Number.isFinite(Number(s)) );

/**
 * Displaying a databound text value consist of rendering a label, and the value.
 * 
 * This component is responsible for the composition of the two.
 * 
 * @param props 
 */
const DisplayNumberComponent = (props: Partial<DisplayNumberProps>) => {    
  const {
    className,
    componentId,    
    hideIfNoValue = false,
    useThousand = true,    
    label,
    textLabel,
    valueItem,
    prefix='',
    suffix=''      
    } = props;
        
  const isValidNumber = !!valueItem && isNumeric(valueItem.value);

//Do not render the component if the input is an invalid number and hideIfNoValue is false.
  if (!isValidNumber && !!hideIfNoValue) {
    return null;
  }    
  
  const formattedNumber = !!valueItem && new Intl.NumberFormat(props.locale, {useGrouping: useThousand, 
    maximumFractionDigits: props.maxDecimals}).format(valueItem.value);  
  const alignContent = props.alignToEnd && styles.alignEnd;  

  return (    
      <div className={`${className} ${alignContent}`} id={componentId}>
        <DisplayNumberLabel label={label} textLabel={textLabel} />
        <span className={'value-number'}>
          {formattedNumber ? `${prefix}${formattedNumber}${suffix ? ' ' : ''}${suffix}` : <NoValueDash/>}
        </span>
      </div>
  );
};

/**
 * A utility that maps known props to a known style and appends them to a list.
 * 
 * The list of final style classes are concatenated into a valid JSX className string attribute
 * 
 * @param props 
 */
const styleFromProps = (props: DisplayNumberProps) =>
  [
    styles.number,
    props.className,
    styles[props.style] && styles[props.style],    
    props.showStacked && styles.stackedColumn,    
    props.strong && styles.strong,
    props.invertStrong && 'invert-strong'
  ].filter(s => s).join(' ');

/**
 * Higher order component to override the final className
 *
 * @param Component the component to decorate
 */
const styleVariations = (Component: any) => ({children, ...childProps}: DisplayNumberProps) => (
  <Component {...childProps} className={styleFromProps(childProps)}>
    {children}
  </Component>
);

export const DisplayNumber = compose(styleVariations)(DisplayNumberComponent);
